import React, { useEffect, useCallback } from 'react';
import PlyerWithClosedCaptions from '@lwtears/lwt-common-frontend/lib/PlayerWithClosedCaptions';
import ApplicationIFrame from '@lwtears/lwt-common-frontend/lib/ApplicationFrame';
import {
  PlyrOptions,
  CCPlyrProps,
} from '@lwtears/lwt-common-frontend/lib/PlayerWithClosedCaptions/Player/Player';
import { getLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { useLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import { useA11ySettings } from '@lwtears/lwt-common-frontend/lib/@a11y/A11ySettings/state/use-a11y-settings';
import { DraggableIframeWrapper } from './DraggableIframeWrapper';
import { AssetType } from '../../../graph/schema-generated-types-educator';

enum ResourceTypeIds {
  DYK = 1,
  WALL_CARD = 2,
}

type Props = {
  loadedData: () => any;
  showCloseIcon?: boolean;
  selectedResource: {
    data: any;
    url: string;
    title: string;
    mediaType: string;
    aspectRatio?: string;
    heightAdjust?: number;
    type: { type: string };
    src: string;
    vtt: string;
    narration: {
      src: string;
      vtt: string;
    };
  };
  plyrOptions?: Partial<PlyrOptions>;
  fullScreen?: boolean;
};

const ResourcePlayer: React.FC<Props> = ({
  selectedResource: { data, aspectRatio = '1.5', mediaType, narration, ...selectedResource },
  loadedData = () => null,
  fullScreen = false,
}) => {
  const [lwtStoreState, lwtStoreDispatch] = useLwtStore();
  const [a11ySettings] = useA11ySettings();
  const resourceMediaType = data?.mediaType ?? mediaType;
  const resourceUrl = selectedResource?.src ?? selectedResource.url;
  const resourceHeightAdjust = data?.heightAdjust ?? selectedResource.heightAdjust;
  const userSettingVolume = a11ySettings.audioSettings?.volume * 0.01;

  const shouldAutoPlay = ![ResourceTypeIds.DYK, ResourceTypeIds.WALL_CARD].includes(
    (selectedResource as any).type?.id
  );

  const PlyrProps: CCPlyrProps = {
    id: 'lwt-resource-player',
    mimeType: '',
    aspectRatio: data?.aspectRatio ?? aspectRatio,
    src: resourceUrl,
    type: resourceMediaType,
    title: selectedResource.title,
    onLoad: loadedData,
    narration: {
      ...narration,
      captions: [{ src: narration?.vtt ?? '', label: selectedResource.title, kind: 'captions' }],
    },
    options: {
      withNativeControls: true,
      locale: getLocale(),
      captionButton: !!selectedResource.vtt,
      captions: {
        active: a11ySettings.closedCaptionsSettings?.showClosedCaptions ?? false,
        styles: {
          fontSize: a11ySettings.closedCaptionsSettings?.closedCaptionSize ?? 16,
          marginBottom: '21px',
        },
      },
      showSettings: !!narration?.src,
      autoplay: shouldAutoPlay,
    },
    captions: [
      { kind: 'captions', src: selectedResource.vtt, label: selectedResource.title },
    ] as any,
    config: { volume: userSettingVolume ?? 0.5 },
  };

  const updateLiteRouteSetting = useCallback(async (isLiteRoute) => {
    return await lwtStoreDispatch.setSettings({
      ...lwtStoreState.settings,
      accessibility: a11ySettings,
      isLiteRoute,
    });
  }, []);

  useEffect(() => {
    updateLiteRouteSetting(true);
    return function undo() {
      updateLiteRouteSetting(false);
    };
  }, []);

  const AVPlayer = () => {
    return <PlyerWithClosedCaptions {...PlyrProps} />;
  };

  const Iframe = () => {
    return (
      <DraggableIframeWrapper>
        <ApplicationIFrame
          onLoad={loadedData}
          aspectRatio={PlyrProps.aspectRatio as string}
          heightAdjust={resourceHeightAdjust}
          url={resourceUrl}
          title={selectedResource.title ?? ''}
          options={{ fullScreen }}
        />
      </DraggableIframeWrapper>
    );
  };
  const Render = () => {
    switch (resourceMediaType as AssetType | undefined) {
      case undefined:
      case AssetType.PDF: {
        return <Iframe />;
      }
      default: {
        return <AVPlayer />;
      }
    }
  };

  return <Render />;
};

export default ResourcePlayer;
